import type { GraphicsQuality } from "../render/scene";
import { clearCacheAndReload } from "./cacheUtils";
import { loadGraphicsQuality, saveGraphicsQuality } from "./graphicsSettings";
import { loadSettings, saveSettings, type SolitaireSettings } from "./settings";

const GRAPHICS_QUALITY_LABELS: Record<GraphicsQuality, string> = {
  high: "Élevée",
  standard: "Standard",
  battery: "Économie",
};

/**
 * Modale de reglages, autonome (construit son propre calque par-dessus `root`) :
 * ouvrable depuis le menu principal comme depuis le HUD en cours de partie.
 * Regroupe la qualite graphique (cle partagee entre tous les jeux du Taverneu)
 * et les preferences propres au solitaire (tirage 1/3, chrono, surbrillance).
 * Le changement de mode de tirage ne prend effet qu'a la prochaine donne.
 */
export function openSettingsModal(
  root: HTMLElement,
  onGraphicsChange: (quality: GraphicsQuality) => void,
  onSettingsChange: (settings: SolitaireSettings) => void,
): void {
  const quality = loadGraphicsQuality();
  const settings = loadSettings();

  const overlay = document.createElement("div");
  overlay.className = "menu-modal";
  const qualityButtons = (Object.keys(GRAPHICS_QUALITY_LABELS) as GraphicsQuality[])
    .map(
      (q) =>
        `<button type="button" data-quality="${q}" class="${q === quality ? "active" : ""}">${GRAPHICS_QUALITY_LABELS[q]}</button>`,
    )
    .join("");
  overlay.innerHTML = `
    <div class="parchment-panel">
      <h2>Réglages</h2>
      <p><strong>Qualité graphique</strong></p>
      <div class="segmented" id="quality-seg">${qualityButtons}</div>
      <p><strong>Pioche</strong> <span class="setting-note">(à la prochaine donne)</span></p>
      <div class="segmented" id="draw-seg">
        <button type="button" data-draw="3" class="${settings.draw3 ? "active" : ""}">3 cartes</button>
        <button type="button" data-draw="1" class="${settings.draw3 ? "" : "active"}">1 carte</button>
      </div>
      <p><strong>Confort</strong></p>
      <label class="setting-toggle"><input type="checkbox" id="opt-timer" ${settings.showTimer ? "checked" : ""}/> Afficher le chrono</label>
      <label class="setting-toggle"><input type="checkbox" id="opt-highlight" ${settings.highlight ? "checked" : ""}/> Surbrillance des coups possibles</label>
      <p><strong>Mise à jour</strong></p>
      <button class="wood-button" id="clear-cache-btn" type="button">Vider le cache et actualiser</button>
      <button class="wood-button" id="settings-modal-close" type="button">Fermer</button>
    </div>
  `;
  root.appendChild(overlay);

  const close = (): void => overlay.remove();
  overlay.querySelector<HTMLButtonElement>("#settings-modal-close")!.addEventListener("click", close);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("#clear-cache-btn")!.addEventListener("click", (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Nettoyage...";
    void clearCacheAndReload();
  });

  const qualityEls = overlay.querySelectorAll<HTMLButtonElement>("#quality-seg button");
  qualityEls.forEach((button) => {
    button.addEventListener("click", () => {
      const q = button.dataset.quality as GraphicsQuality;
      saveGraphicsQuality(q);
      qualityEls.forEach((b) => b.classList.toggle("active", b === button));
      onGraphicsChange(q);
    });
  });

  const persist = (): void => {
    saveSettings(settings);
    onSettingsChange({ ...settings });
  };

  const drawEls = overlay.querySelectorAll<HTMLButtonElement>("#draw-seg button");
  drawEls.forEach((button) => {
    button.addEventListener("click", () => {
      settings.draw3 = button.dataset.draw === "3";
      drawEls.forEach((b) => b.classList.toggle("active", b === button));
      persist();
    });
  });

  overlay.querySelector<HTMLInputElement>("#opt-timer")!.addEventListener("change", (e) => {
    settings.showTimer = (e.currentTarget as HTMLInputElement).checked;
    persist();
  });
  overlay.querySelector<HTMLInputElement>("#opt-highlight")!.addEventListener("change", (e) => {
    settings.highlight = (e.currentTarget as HTMLInputElement).checked;
    persist();
  });
}
